// export { default } from '@/components/PackagePage';

import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import React from 'react';
import { useRouter } from 'next/router';
import { LoadingIndicator } from '@qga/roo-ui/components';

import { logger } from '@shared/utils';
import { InViewProvider } from '@shared/v2/components/InView';
import { DataLayerProvider } from '@shared/v2/contexts/DataLayerContext';

import { ProcuredOfferProvider } from '@/components/PackagePage/hooks/useProcuredOffer';
import { DestinationProvider } from '@/hooks/useDestination';
import { Meta } from '@/v2/components/global/Meta';
import { Layout } from '@/v2/components/layout/Layout';
import { PackageView } from '@/v2/components/pages/PackageView';
import { FlightModalProvider } from '@/v2/components/pages/PackageView/contexts/FlightModalContext';
import { OfferModalControlsProvider } from '@/v2/components/pages/PackageView/contexts/OfferModalControlsContext';
import { OtherPackageModalProvider } from '@/v2/components/pages/PackageView/contexts/OtherPackageModalContext';
import { RoomOptionsDataProvider } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { SearchPanelControlsProvider } from '@/v2/components/pages/PackageView/contexts/SearchPanelControlsContext';
import { SearchPanelDataProvider } from '@/v2/components/pages/PackageView/contexts/SearchPanelDataContext';
import { SectionControlsProvider } from '@/v2/components/pages/PackageView/contexts/SectionControlsContext/SectionControlsContext';
import { getPackagePage } from '@/v2/services/bff/getPackagePage';
import PropertyContentProvider from '../../../../../components/PackagePage/hooks/usePropertyContent/PropertyContentProvider';

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  params,
  preview,
}: GetStaticPropsContext<{
  destinationName: string;
  propertyId: string;
  params: string[];
}>) => {
  try {
    const props = await getPackagePage({
      destinationName: params?.destinationName,
      propertyId: params?.propertyId,
      preview,
    });

    return {
      props,
      revalidate: 300,
    };
  } catch (error) {
    logger.error(error);
    return {
      props: {
        app: null,
        campaign: null,
        propertyContent: null,
        procuredOffer: null,
        meta: null,
        navigation: null,
        maintenanceMessage: null,
      },
      notFound: true,
      revalidate: 300,
    };
  }
};

const PackagePage = ({
  app,
  campaign,
  propertyContent,
  procuredOffer,
  navigation,
  meta,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const dataLayerData = { pageName: 'Package Page' };

  if (router.isFallback) {
    return <LoadingIndicator />;
  }

  return (
    propertyContent && (
      <PropertyContentProvider value={propertyContent}>
        <DestinationProvider value={propertyContent.destination}>
          <ProcuredOfferProvider value={procuredOffer}>
            <DataLayerProvider value={dataLayerData}>
              <InViewProvider>
                <SearchPanelDataProvider>
                  <SearchPanelControlsProvider>
                    <SectionControlsProvider>
                      <FlightModalProvider>
                        <OtherPackageModalProvider>
                          <RoomOptionsDataProvider
                            propertyContent={propertyContent}
                            procuredOffer={procuredOffer}
                          >
                            <OfferModalControlsProvider>
                              <Meta
                                title={meta.title}
                                description={meta.description}
                              />
                              <Layout app={app} navigation={navigation}>
                                <PackageView
                                  campaign={campaign}
                                  propertyContent={propertyContent}
                                  procuredOffer={procuredOffer}
                                />
                              </Layout>
                            </OfferModalControlsProvider>
                          </RoomOptionsDataProvider>
                        </OtherPackageModalProvider>
                      </FlightModalProvider>
                    </SectionControlsProvider>
                  </SearchPanelControlsProvider>
                </SearchPanelDataProvider>
              </InViewProvider>
            </DataLayerProvider>
          </ProcuredOfferProvider>
        </DestinationProvider>
      </PropertyContentProvider>
    )
  );
};

export default PackagePage;
